import { createApp } from "vue";
import App from "./App.vue";
import FriendGateView from "./views/FriendGateView.vue";
import { router } from "./router.js";
import { ensureLiffReady, getInitialScreenFromUrl, isOaFriend } from "./liff.js";
import { loadCurrentArchive } from "./store.js";
import "./style.css";

/**
 * Rich-menu deep links carry `?screen=` to open a specific tab on launch (the
 * hash router can't be targeted by a path — see getInitialScreenFromUrl). Only
 * an allow-listed set maps to a route, so a stray/unknown value just falls
 * through to the default archive screen instead of erroring.
 */
const SCREEN_ROUTES: Record<string, string> = {
  settings: "/settings",
  trash: "/trash",
  plans: "/plans",
};

async function bootstrap() {
  await ensureLiffReady();
  // ensureLiffReady triggers a redirect via liff.login() when not logged
  // in yet; the code below simply won't run until the redirect back lands
  // here with a valid session.

  // Adding the OA is a hard precondition, not an upsell — see isOaFriend().
  // The gate replaces the app entirely rather than overlaying it, so no API
  // call (and no archive creation) happens for someone who never gets in.
  if (!(await isOaFriend())) {
    const gate = createApp(FriendGateView, {
      onAdded: () => {
        gate.unmount();
        startApp();
      },
    });
    gate.mount("#app");
    return;
  }

  startApp();
}

/** Mounts the real app. Reached only past the friend gate. */
function startApp() {
  void loadCurrentArchive();

  const app = createApp(App);
  app.use(router);

  // Honor a rich-menu ?screen= deep link before the first paint so the user
  // lands directly on e.g. Settings rather than the archive flashing first.
  const screen = getInitialScreenFromUrl();
  const target = screen ? SCREEN_ROUTES[screen] : undefined;
  if (target) router.replace(target);

  app.mount("#app");
}

bootstrap().catch((err) => {
  // eslint-disable-next-line no-console
  console.error("BarkUp liff: failed to start", err);
  document.body.innerHTML = `<p style="padding:24px;font-family:sans-serif">โหลดแอปไม่สำเร็จ กรุณาลองใหม่ในแอป LINE</p>`;
});
